import { useRef, type CSSProperties, type KeyboardEvent, type MouseEvent } from "react";

import { gapText, seekTargetMs, timecode, type Segment, type Timeline } from "./timeline.js";

/**
 * The scrubber: the session's whole timeline in the well, with every hole in it attributed.
 *
 * **A gap is drawn, not skipped.** The recording is a record of what happened, and four
 * minutes where the screen was locked is part of what happened. Each gap is a 12px hatch on
 * the 4px rail (design-spec §4 "Video well + scrubber") with its label below in one form,
 * `4 min · paused by Maricel` (P5): under the gap when it is the only one, in the gap list
 * under the controls when there are several. Capture lost is the one red gap
 * (`--problem-on-well`); every other cause is the `--gap-hatch` line. The hatch is painted in
 * `currentColor`, so the segment's `color` is its paint (e2e `session.spec.ts`, issue 200 P5).
 * The reason also reaches the page in `data-gap-reason`, in `title` for a pointer and in
 * `aria-label`. (`role="slider"` is on the ARIA spec's children-presentational list, so the
 * segment names inside it are dropped for a screen reader; the visible labels are what carry
 * the reasons to one.)
 *
 * **A gap cannot reach this component without a reason.** `timelineOf` refuses an
 * unattributed hole, and `Segment`'s `gap` arm has no optional reason, so there is no value
 * shaped like an unexplained gap to render.
 *
 * **On a timeline with no gaps, nothing is said under the rail** (D12: normal states carry no
 * sentence). It once printed a line about the timeline, and before that one about the whole
 * recording, which no hole set the player holds could pay for.
 */
export interface ScrubberProps {
  timeline: Timeline;
  positionMs: number;
  onSeek(positionMs: number): void;
  /** Who recorded, so a pause names them. */
  person?: string;
  /** `false` for stills: the rail is drawn and does not drag (they step instead). */
  interactive?: boolean;
}

/** The step an arrow key takes: five seconds, or a hundredth of a short session. */
function stepMs(durationMs: number): number {
  return Math.max(1000, Math.min(5000, Math.round(durationMs / 100)));
}

type Gap = Extract<Segment, { kind: "gap" }>;

function gapsOf(timeline: Timeline): Gap[] {
  return timeline.segments.filter((segment): segment is Gap => segment.kind === "gap");
}

/** The hatch for one gap, painted in the segment's `color`: one diagonal, or crossed when lost. */
function hatch(lost: boolean): CSSProperties {
  const one = "repeating-linear-gradient(135deg, currentColor 0 2px, transparent 2px 5px)";
  return {
    color: lost ? "var(--problem-on-well)" : "var(--gap-hatch)",
    background: lost
      ? "repeating-linear-gradient(45deg, currentColor 0 2px, transparent 2px 4px), repeating-linear-gradient(135deg, currentColor 0 2px, transparent 2px 4px)"
      : one,
    boxShadow: "inset 0 0 0 1px currentColor",
    borderRadius: "var(--radius-bar)",
  };
}

const pct = (ms: number, total: number) => `${(Math.min(Math.max(ms, 0), total) / total) * 100}%`;

export function Scrubber({ timeline, positionMs, onSeek, person, interactive = true }: ScrubberProps) {
  const railRef = useRef<HTMLDivElement>(null);
  const total = timeline.durationMs;
  const gaps = gapsOf(timeline);
  const at = pct(positionMs, total);

  const seekToFraction = (fraction: number) => onSeek(seekTargetMs(timeline, fraction));

  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    const rail = railRef.current;
    if (!rail) return;
    // The rail has no border, so its laid-out box is the box the segments are placed
    // across. Its own fractional width, not the integer `clientWidth`: the rounding read a
    // click at 0:30 as 0:29 (e2e `session.spec.ts`, the epoch boundary test).
    const box = rail.getBoundingClientRect();
    if (box.width <= 0) return;
    seekToFraction((event.clientX - box.left) / box.width);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = stepMs(total);
    const move = (to: number) => {
      event.preventDefault();
      onSeek(seekTargetMs(timeline, Math.min(Math.max(to, 0), total) / total));
    };
    if (event.key === "ArrowRight") move(positionMs + step);
    else if (event.key === "ArrowLeft") move(positionMs - step);
    else if (event.key === "Home") move(0);
    else if (event.key === "End") move(total);
  };

  const slider = interactive
    ? {
        "data-scrubber": "",
        role: "slider",
        tabIndex: 0,
        "aria-label": "Playhead",
        "aria-valuemin": 0,
        "aria-valuemax": Math.round(total / 1000),
        "aria-valuenow": Math.round(positionMs / 1000),
        "aria-valuetext": timecode(positionMs),
        onClick,
        onKeyDown,
      }
    : { "data-rail": "" };

  const only = gaps.length === 1 ? gaps[0]! : null;

  return (
    <div
      ref={railRef}
      {...slider}
      style={{
        position: "relative",
        flex: 1,
        minWidth: 0,
        height: 30,
        marginBottom: only ? 18 : 0,
        cursor: interactive ? "pointer" : "default",
        borderRadius: "var(--radius-focus)",
      }}
    >
      <span
        aria-hidden="true"
        style={{ position: "absolute", left: 0, right: 0, top: 13, height: 4, borderRadius: "var(--radius-bar)", background: "var(--well-rail)" }}
      />
      <span
        data-rail-played=""
        aria-hidden="true"
        style={{ position: "absolute", left: 0, top: 13, height: 4, width: at, borderRadius: "var(--radius-bar)", background: "var(--well-ink)" }}
      />
      {gaps.map((segment) => {
        const label = gapText(segment, person);
        return (
          <span
            key={`g${segment.startMs}`}
            data-segment="gap"
            data-gap-reason={segment.reason}
            title={label}
            aria-label={label}
            style={{
              position: "absolute",
              top: 9,
              height: 12,
              left: pct(segment.startMs, total),
              width: pct(segment.endMs - segment.startMs, total),
              ...hatch(segment.reason === "capture_lost"),
            }}
          />
        );
      })}
      <span
        data-playhead=""
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 7,
          left: at,
          width: 16,
          height: 16,
          marginLeft: -8,
          borderRadius: "var(--radius-round)",
          background: "var(--well-head)",
          boxShadow: "0 0 0 3px var(--well)",
          pointerEvents: "none",
        }}
      />
      {only && (
        // Under its gap, reading away from the nearer end of the rail: from its start in the
        // first half, back from its end in the second. Either way the label has at least half
        // the rail to run in, so it is not clipped by the well.
        <span
          data-gap-label=""
          style={{
            position: "absolute",
            top: 30,
            ...(only.startMs / total > 0.5
              ? { right: `${(1 - Math.min(only.endMs, total) / total) * 100}%` }
              : { left: pct(only.startMs, total) }),
            whiteSpace: "nowrap",
            fontSize: "var(--type-small-size)",
            lineHeight: "var(--type-small-line)",
            color: "var(--well-mute)",
          }}
        >
          {gapText(only, person)}
        </span>
      )}
    </div>
  );
}

/**
 * Several gaps, listed under the controls: a swatch in the gap's own hatch, where it starts on
 * the recording, and its label (variant E `.ve-gaps`, screen-catalog `session-gaps`). One gap
 * is labeled under itself on the rail instead, and none says nothing.
 */
export function GapList({ timeline, person }: { timeline: Timeline; person?: string }) {
  const gaps = gapsOf(timeline);
  if (gaps.length < 2) return null;
  return (
    <ul
      data-gap-legend=""
      style={{
        listStyle: "none",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
        columnGap: "var(--space-3)",
        rowGap: "var(--space-05)",
        margin: "10px 0 0 50px",
        padding: 0,
        fontSize: "var(--type-small-size)",
        lineHeight: "var(--type-small-line)",
        color: "var(--well-mute)",
      }}
    >
      {gaps.map((segment) => {
        const lost = segment.reason === "capture_lost";
        return (
          <li
            key={`l${segment.startMs}`}
            data-gap-legend-item=""
            data-gap-reason={segment.reason}
            style={{ display: "flex", alignItems: "center", gap: "var(--space-1)", minWidth: 0 }}
          >
            <span aria-hidden="true" style={{ display: "block", flex: "none", width: 22, height: 10, ...hatch(lost) }} />
            {/* A position on the recording, never a clock time (issue 191). */}
            <span data-elapsed="" style={{ color: "var(--well-ink)", minWidth: 38 }}>
              {timecode(segment.startMs)}
            </span>
            <span
              data-gap-label=""
              style={lost ? { color: "var(--well-ink)", fontWeight: 600 } : undefined}
            >
              {gapText(segment, person)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
